import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Shield, Lock, Eye, Database, Globe, Bell } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <main className="container mx-auto px-6 pt-32 pb-24 max-w-4xl">
        <div className="bg-white rounded-[2rem] shadow-xl shadow-blue-900/5 p-8 md:p-16 border border-slate-200">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center text-blue-600">
              <Shield className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight">Privacy Policy</h1>
              <p className="text-slate-500 font-medium">Last Updated: April 13, 2026</p>
            </div>
          </div>

          <div className="prose prose-slate max-w-none">
            <p className="text-lg text-slate-600 leading-relaxed mb-8">
              At DevelopmentAnalyzer, we provide sophisticated AI-powered property intelligence. Your privacy and the security of your data are fundamental to our "Enterprise Intelligence" standard. This policy explains how we collect, use, and safeguard your information.
            </p>

            <section className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <Database className="w-5 h-5 text-blue-600" />
                <h2 className="text-xl font-bold text-slate-900 m-0">1. Data We Collect</h2>
              </div>
              <p className="text-slate-600 leading-relaxed">
                To provide precise analyses, we collect information you provide directly, including:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2 mt-4">
                <li><strong>Account Information</strong>: Name, email address, and professional affiliation.</li>
                <li><strong>Property Queries</strong>: Addresses or APN (Assessor Parcel Numbers) you submit for analysis.</li>
                <li><strong>Usage Data</strong>: Information on how you interact with our GIS tools and reports.</li>
              </ul>
            </section>

            <section className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <Globe className="w-5 h-5 text-blue-600" />
                <h2 className="text-xl font-bold text-slate-900 m-0">2. How We Use Information</h2>
              </div>
              <p className="text-slate-600 leading-relaxed">
                Your data is used strictly to enhance our analytics and provide you with a personalized experience:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2 mt-4">
                <li>Processing and delivering AI-powered buildability reports.</li>
                <li>Verifying jurisdictional GIS data for your specific queries.</li>
                <li>Improving our machine learning models for higher accuracy (anonymized data only).</li>
              </ul>
            </section>

            <section className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <Lock className="w-5 h-5 text-blue-600" />
                <h2 className="text-xl font-bold text-slate-900 m-0">3. Data Security</h2>
              </div>
              <div className="bg-blue-50 border-l-4 border-blue-600 p-6 rounded-r-xl italic text-blue-900">
                "We implement industry-standard AES-256 encryption for all data at rest and TLS 1.3 for all data in transit."
              </div>
              <p className="text-slate-600 leading-relaxed mt-4">
                We use secure cloud infrastructure to protect against unauthorized access, alteration, or destruction of your professional data.
              </p>
            </section>

            <section className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <Eye className="w-5 h-5 text-blue-600" />
                <h2 className="text-xl font-bold text-slate-900 m-0">4. Third-Party Sharing</h2>
              </div>
              <p className="text-slate-600 leading-relaxed">
                DevelopmentAnalyzer does not sell your personal information. We only share data with service providers necessary to operate our platform (e.g., payment processors, GIS hosting) or when required by California state law.
              </p>
            </section>

            <section className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <Bell className="w-5 h-5 text-blue-600" />
                <h2 className="text-xl font-bold text-slate-900 m-0">5. Changes to This Policy</h2>
              </div>
              <p className="text-slate-600 leading-relaxed">
                We may update this policy periodically. We will notify you of any significant changes by posting the new policy on this page and updating the "Last Updated" date.
              </p>
            </section>

            <div className="bg-slate-100 p-8 rounded-2xl mt-12 text-center text-slate-500 font-medium">
              If you have any questions regarding our privacy practices, please contact us at <a href="mailto:support@privateopenhouse.com" className="text-blue-600 underline">support@privateopenhouse.com</a>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
